/**
 * Client for the subprocessor-change / legal-notice announcements of organization-api-v2
 * (`/v2/internal/announcements*`).
 *
 * The admin portal calls these endpoints with its internal-auth token through the already
 * authenticated organization-v2 axios instance (same base URL + bearer interceptor).
 *
 * TODO: once @epilot/organization-client-internal ships the announcement operations, replace the
 * raw axios calls and the local types below with the typed operations
 * (e.g. `client.listAnnouncementsInternal()`) and `OrganizationComponents.Schemas.Announcement*`.
 * The exported function signatures are meant to stay the same, so only this file has to change.
 */
import { getOrganizationClient } from './organization-client'

export const ANNOUNCEMENT_TYPES = ['subprocessor_change'] as const
export type AnnouncementType = (typeof ANNOUNCEMENT_TYPES)[number]

export type AnnouncementStatus = 'draft' | 'published' | 'archived'

export const ANNOUNCEMENT_ORGANIZATION_STATUSES = [
  'acknowledged',
  'pending',
  'expired',
  'superseded'
] as const
export type AnnouncementOrganizationStatusValue =
  (typeof ANNOUNCEMENT_ORGANIZATION_STATUSES)[number]

export interface AnnouncementMessage {
  de: string
  en?: string
}

export interface Announcement {
  id: string
  type: AnnouncementType
  message: AnnouncementMessage
  link_url: string
  list_version: string
  /** YYYY-MM-DD */
  effective_date: string
  window_days: number
  status: AnnouncementStatus
  created_at: string
  created_by?: string
  updated_at?: string
  published_at?: string
  published_by?: string
  expires_at?: string
}

export interface AnnouncementSummary {
  eligible: number
  acknowledged: number
  pending: number
  expired: number
  superseded: number
}

export type AnnouncementWithSummary = Announcement & {
  summary?: AnnouncementSummary
}

export interface AnnouncementInput {
  type: AnnouncementType
  message: AnnouncementMessage
  link_url: string
  list_version: string
  effective_date: string
  window_days?: number
}

export interface AnnouncementAcknowledgedBy {
  user_id: string
  name?: string
  email?: string
}

export interface AnnouncementOrganizationStatus {
  announcement_id: string
  org_id: string
  org_name?: string
  status: AnnouncementOrganizationStatusValue
  acknowledged_by?: AnnouncementAcknowledgedBy
  acknowledged_at?: string
  portal?: string
  list_version?: string
}

export interface AnnouncementOrganizationsResponse {
  results: AnnouncementOrganizationStatus[]
  summary?: AnnouncementSummary
}

const BASE_PATH = '/v2/internal/announcements'

const announcementPath = (announcementId: string, suffix = '') =>
  `${BASE_PATH}/${encodeURIComponent(announcementId)}${suffix}`

export const listAnnouncements = async (): Promise<
  AnnouncementWithSummary[]
> => {
  const res = await getOrganizationClient().get<{
    results?: AnnouncementWithSummary[]
  }>(BASE_PATH)

  return res.data?.results ?? []
}

export const getAnnouncement = async (
  announcementId: string
): Promise<AnnouncementWithSummary> => {
  const res = await getOrganizationClient().get<AnnouncementWithSummary>(
    announcementPath(announcementId)
  )

  return res.data
}

export const createAnnouncement = async (
  input: AnnouncementInput
): Promise<Announcement> => {
  const res = await getOrganizationClient().post<Announcement>(BASE_PATH, input)

  return res.data
}

export const updateAnnouncement = async (
  announcementId: string,
  input: Partial<AnnouncementInput>
): Promise<Announcement> => {
  const res = await getOrganizationClient().patch<Announcement>(
    announcementPath(announcementId),
    input
  )

  return res.data
}

export const publishAnnouncement = async (
  announcementId: string
): Promise<Announcement> => {
  const res = await getOrganizationClient().post<Announcement>(
    announcementPath(announcementId, '/publish')
  )

  return res.data
}

export const archiveAnnouncement = async (
  announcementId: string
): Promise<Announcement> => {
  const res = await getOrganizationClient().post<Announcement>(
    announcementPath(announcementId, '/archive')
  )

  return res.data
}

export const listAnnouncementOrganizations = async (
  announcementId: string,
  status?: AnnouncementOrganizationStatusValue
): Promise<AnnouncementOrganizationsResponse> => {
  const res =
    await getOrganizationClient().get<AnnouncementOrganizationsResponse>(
      announcementPath(announcementId, '/organizations'),
      { params: { format: 'json', ...(status ? { status } : {}) } }
    )

  return { results: res.data?.results ?? [], summary: res.data?.summary }
}

export const exportAnnouncementOrganizationsCsv = async (
  announcementId: string,
  status?: AnnouncementOrganizationStatusValue
): Promise<Blob> => {
  const res = await getOrganizationClient().get<Blob>(
    announcementPath(announcementId, '/organizations'),
    {
      params: { format: 'csv', ...(status ? { status } : {}) },
      headers: { accept: 'text/csv' },
      responseType: 'blob'
    }
  )

  return res.data
}
